// Escolha de alvos no mapa para as cartas de loot. Tudo sai da lista de ações
// legais (ou da validação do motor), então a UI nunca oferece algo inválido.
import { checkAction, neighborsOf, type Action, type LootKind, type PlayerView } from '@estandarte/engine';
import { ofType } from './actions';
import { territoryName } from '../theme';

export interface LootStep {
  /** Territórios clicáveis neste passo. */
  clickable: Set<string>;
  /** Já escolhidos (aparecem marcados). */
  marked: Set<string>;
  hint: string;
  /** Resultado de clicar num território: novos picks ou uma ação pronta. */
  pick(t: string): { picks?: string[]; action?: Action };
  /** Ação para o botão "Confirmar", quando a carta já pode ser jogada. */
  confirm: Action | null;
  /** Teleporte: limite de tropas depois de escolher origem e destino. */
  countMax: number;
}

const SINGLE: Partial<Record<LootKind, string>> = {
  strike: 'Clique no território inimigo vizinho a um seu (−2 tropas).',
  paralyze: 'Clique no território inimigo a paralisar.',
  blessing: 'Clique no território seu a abençoar.',
  ward: 'Clique no território seu que vai ganhar Stone Skin.',
  restore: 'Clique num território seu que foi atacado (+3 tropas).',
};

export function lootStep(view: PlayerView, me: number, legal: Action[], kind: LootKind, picks: string[]): LootStep {
  const plays = ofType(legal, 'playLoot').filter((a) => a.kind === kind);
  const step: LootStep = { clickable: new Set(), marked: new Set(picks), hint: '', pick: () => ({}), confirm: null, countMax: 0 };

  if (SINGLE[kind]) {
    for (const a of plays) step.clickable.add(a.territory!);
    step.hint = SINGLE[kind]!;
    step.pick = (t) => ({ action: plays.find((a) => a.territory === t) });
    return step;
  }

  if (kind === 'wall') {
    if (!picks.length) {
      for (const a of plays) {
        step.clickable.add(a.from!);
        step.clickable.add(a.to!);
      }
      step.hint = 'Magic Wall: clique na primeira ponta da ligação a bloquear.';
      step.pick = (t) => ({ picks: [t] });
    } else {
      const first = picks[0];
      for (const a of plays) {
        if (a.from === first) step.clickable.add(a.to!);
        if (a.to === first) step.clickable.add(a.from!);
      }
      step.hint = `Magic Wall a partir de ${territoryName(first)}: clique no vizinho do outro lado.`;
      step.pick = (t) => ({
        action: plays.find((a) => (a.from === first && a.to === t) || (a.to === first && a.from === t)),
      });
    }
    return step;
  }

  if (kind === 'teleport') {
    if (picks.length === 0) {
      for (const a of plays) step.clickable.add(a.from!);
      step.hint = 'Teleporte: clique na origem.';
      step.pick = (t) => ({ picks: [t] });
    } else if (picks.length === 1) {
      for (const a of plays) if (a.from === picks[0]) step.clickable.add(a.to!);
      step.hint = `Teleporte de ${territoryName(picks[0])}: clique no destino (qualquer território seu).`;
      step.pick = (t) => ({ picks: [picks[0], t] });
    } else {
      const xs = plays.filter((a) => a.from === picks[0] && a.to === picks[1]);
      step.countMax = xs.length ? Math.max(...xs.map((a) => a.count!)) : 0;
      step.hint = `Teleporte de ${territoryName(picks[0])} para ${territoryName(picks[1])}.`;
    }
    return step;
  }

  if (kind === 'barrage') {
    if (!picks.length) {
      for (const a of plays) step.clickable.add(a.territory!);
      // territórios seus com algum inimigo vizinho, mesmo que só com 1 tropa
      for (const t of Object.keys(view.territories)) {
        if (view.territories[t].owner !== me) continue;
        if (neighborsOf(view, t).some((n) => view.territories[n].owner !== me)) step.clickable.add(t);
      }
      step.hint = 'Great Fireball: clique no território seu de onde sai o fogo.';
      step.pick = (t) => ({ picks: [t] });
      return step;
    }
    const [source, ...targets] = picks;
    const enemies = neighborsOf(view, source).filter((n) => view.territories[n].owner !== me);
    const max = view.info.ruleset.barrageTargets;
    for (const n of enemies) if (targets.includes(n) || targets.length < max) step.clickable.add(n);
    step.marked = new Set(targets);
    step.hint = `Great Fireball a partir de ${territoryName(source)}: escolha até ${max} inimigos vizinhos (${targets.length} escolhidos).`;
    step.pick = (t) => ({ picks: [source, ...(targets.includes(t) ? targets.filter((x) => x !== t) : [...targets, t])] });
    if (targets.length) {
      const action: Action = { type: 'playLoot', player: me, kind, territory: source, targets };
      step.confirm = checkAction(view, action) === null ? action : null;
    }
    return step;
  }

  step.hint = '';
  return step;
}
